import { FC, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { setMessages, useTranslateState } from '@edikk-edikk/redux';
import { IntlProvider, ReactIntlErrorCode } from 'react-intl';
import { MessagesType } from '@edikk-edikk/shared';
import { DiContainerProvider } from '@edikk-edikk/di-container';
import { Container } from 'inversify';
import { Helmet } from '@edikk-edikk/helmet';
import loadable from '@loadable/component';
import { useIsProduction } from '@edikk-edikk/hooks';
import { useAppDispatch } from '#components/redux/hooks';
import { App } from '#base/containers/App';

type PropsType = {
  diContainer: Container;
};

const GlobalError = loadable(() => import('@edikk-edikk/country-layout-black/error/GlobalError'), {
  resolveComponent: ({ GlobalError: Component }) => Component,
});

const AppWrapper: FC<PropsType> = ({
  diContainer,
}) => {
  const { messages, language, defaultLanguage } = useTranslateState();
  const dispatch = useAppDispatch();
  const isProduction = useIsProduction();

  useEffect(() => {
    if (messages) {
      return;
    }

    import(`web/translate/${language}.json`).then((loadedMessages: MessagesType): void => {
      dispatch(setMessages(loadedMessages));
    }).catch(() => null);
  }, [dispatch, language, messages]);

  return (
    <DiContainerProvider diContainer={diContainer}>
      <ErrorBoundary FallbackComponent={GlobalError}>
        <Helmet htmlAttributes={{ lang: language }} />
        <IntlProvider
          messages={messages}
          defaultLocale={defaultLanguage ?? 'en'}
          locale={language ?? 'en'}
          onError={(intlError) => {
            if (isProduction || intlError.code === ReactIntlErrorCode.MISSING_TRANSLATION) {
              return;
            }
            globalThis.console.warn(intlError.message);
          }}
          defaultRichTextElements={{
            // eslint-disable-next-line react/no-unstable-nested-components
            b: (chunks) => <b>{chunks}</b>,
          }}
        >
          <App />
        </IntlProvider>
      </ErrorBoundary>
    </DiContainerProvider>
  );
};

export { AppWrapper };
